<template>
  <v-container>
    <div class="d-flex justify-end mb-2">
      <v-btn color="primary" @click="showCreateModal"><v-icon left>mdi-book-plus</v-icon>{{ trans('misc.recurring.newMisc') }}</v-btn>
    </div>

    <ajax-table
      ref="recurringMiscsTable"
      :resource="resourceUrl"
      :headers="[
        {text: 'misc.object', value: 'object.name'},
        {text: 'misc.recurring.descriptionTemplate', value: 'descriptionTemplate'},
        {text: 'misc.account', value: 'miscAccount.accountNbr', sortable: false},
        {text: 'misc.receiptField', value: 'receiptField'},
        {text: 'misc.price', value: 'price', type: 'currency'},
        {text: 'misc.tax', value: 'tax', type: 'currency'},
        {text: 'misc.recurring.interval', value: 'interval', sortable: false},
        {text: 'misc.recurring.from', value: 'from', type: 'date', dateTimeFormat: 'MMMM YYYY'},
        {text: 'misc.recurring.to', value: 'to', type: 'date', dateTimeFormat: 'MMMM YYYY'},
        {text: 'general.notes', value: 'notes'},
        {text: '', value: 'actions'},
      ]"
      :page-options="{itemsPerPage: 20}"
      :items-per-page-options="[10, 20, 50]"
      filterable
      :filter="filter"
      no-elevation
    >

      <template v-slot:filters>
        <select-object hide-details :object-id.sync="filterData.objectId" style="max-width: 250px" clearable/>
        <search v-model="filterData.search" />
      </template>

      <template v-slot:item.object.name="{item}">
        <object-name :value="item.object" />
      </template>

      <template v-slot:item.interval="{item}">
        {{ recurringMiscIntervalOptions[item.interval] ? recurringMiscIntervalOptions[item.interval].text : null }}
      </template>

      <template v-slot:item.notes="{item}">
        <div class="text--secondary">{{ item.notes }}</div>
      </template>

      <template v-slot:item.actions="{ item }">
        <context-menu-icon>
          <v-list dense>
            <v-list-item @click="showUpdateModal(item)">
              <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('misc.editMiscObject') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="recurringMiscToDelete = item">
              <v-list-item-icon><v-icon>mdi-delete</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('misc.deleteMiscObject') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </context-menu-icon>
      </template>

    </ajax-table>

    <recurring-misc-addit-modal :initial-recurring-misc="recurringMiscToUpdate" :show-modal.sync="showMiscCreateModal" @success="$refs.recurringMiscsTable.loadData()"/>

    <confirm-deletion-modal
        :title="trans('misc.deleteMiscObject')"
        :show-modal="recurringMiscToDelete != null"
        @confirm="deleteRecurringMisc"
        @close="recurringMiscToDelete = null"
    >
      <template v-if="recurringMiscToDelete">
        <b>{{ trans('misc.description') }}: </b>
        {{ recurringMiscToDelete.descriptionTemplate }}
        <br>
        <b>{{ trans('misc.price') }}: </b>
        <currency-amount style="display: inline-block" :amount="recurringMiscToDelete.price" currency="EUR"/>
      </template>
    </confirm-deletion-modal>

  </v-container>
</template>

<script>
import AjaxTable from './AjaxTable';
import {LocaleUtils} from '../utils/locale-utils';
import ContextMenuIcon from './ContextMenuIcon';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '@/utils/main-utils';
import RecurringMiscAdditModal from '@/components/RecurringMiscAdditModal';
import dayjs from 'dayjs';
import ConfirmDeletionModal from '@/components/ConfirmDeletionModal';
import CurrencyAmount from '@/components/CurrencyAmount';
import {RecurringMiscApi} from '@/api/recurring-misc-api'
import {and, comparison, eq, or} from 'rsql-builder';
import {like} from '@/lib/custom-rsql-operators';
import Search from '@/components/Search';
import SelectObject from '@/components/SelectObject';
import ObjectName from '@/components/ObjectName';
import {FilterUtils} from '@/utils/filter-utils';
import {recurringMiscIntervalOptions} from '../lib/recurring-misc-interval-options';

export default {
  name: 'RecurringMiscs',
  mixins: [
    LocaleUtils,
    DateUtils,
    RouterUtils,
    MainUtils,
    FilterUtils
  ],
  components: {
    ObjectName,
    SelectObject,
    Search,
    CurrencyAmount,
    ConfirmDeletionModal,
    RecurringMiscAdditModal,
    AjaxTable,
    ContextMenuIcon
  },
  data: () => ({
    filterData: {
      search: null,
      objectId: null,
    },
    showMiscCreateModal: false,
    recurringMiscToUpdate: null,
    recurringMiscToDelete: null,
    recurringMiscIntervalOptions
  }),
  computed: {
    filter () {
      let filters =  [];
      if(this.filterData.search) {
        filters.push(this.buildSearchFilter(this.filterData.search, ['descriptionTemplate', 'receiptField', 'miscAccount.accountNbr', 'notes']));
      }
      if(this.filterData.objectId != null) {
        filters.push(comparison('object.id', eq(this.filterData.objectId)))
      }
      return filters;
    },
    resourceUrl () {
      return RecurringMiscApi.apiUri;
    }
  },
  activated () {
    this.$refs.recurringMiscsTable.loadData();
  },
  methods: {
    formatMonth(isoMonth) {
      return dayjs(isoMonth).format('MMMM YYYY');
    },
    showCreateModal() {
      this.recurringMiscToUpdate = null;
      this.showMiscCreateModal = true;
    },
    showUpdateModal(recurringMisc) {
      this.recurringMiscToUpdate = recurringMisc;
      this.showMiscCreateModal = true;
    },
    deleteRecurringMisc() {
      RecurringMiscApi.delete(this.recurringMiscToDelete.id)
        .then(response => {
          this.$refs.recurringMiscsTable.loadData();
        })
        .catch(this.handleErrorResponse);
    }
  }
}
</script>

<style>
</style>
